import { useState, useRef, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Eye, EyeOff, Loader2, MailCheck } from "lucide-react";
import { AuthShell } from "@/components/auth/auth-shell";
import { AuthLegalLinks } from "@/components/auth/auth-legal-links";
import { GoogleSignInButton } from "@/components/auth/google-sign-in-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useMe,
  useRequestCode,
  useVerifyCode,
  useCompleteSignup,
  useGoogleAuth,
} from "@/hooks/use-auth";
import { authErrorMessage } from "@/lib/auth-errors";

function defaultTarget(role?: string | null): string {
  return role === "ADMIN" || role === "FULL_ACCESS" ? "/admin" : "/app";
}

type Step = "email" | "code" | "password";

export default function SignupPage() {
  const navigate = useNavigate();
  const loc = useLocation();
  const next = new URLSearchParams(loc.search).get("next");

  const { data: me, isLoading: meLoading } = useMe();

  const requestCode = useRequestCode();
  const verifyCode = useVerifyCode();
  const completeSignup = useCompleteSignup();
  const googleAuth = useGoogleAuth();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [signupToken, setSignupToken] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Already signed in? Bounce.
  if (!meLoading && me) {
    return <Navigate to={next ?? defaultTarget(me.role)} replace />;
  }

  function goTo(dest: string, mustChangePassword?: boolean) {
    if (mustChangePassword) {
      navigate(`/change-password?next=${encodeURIComponent(dest)}`, { replace: true });
    } else {
      navigate(dest, { replace: true });
    }
  }

  // ── Step 1: request the code ────────────────────────────────────────────────
  async function onRequestCode(e: FormEvent) {
    e.preventDefault();
    if (!email || requestCode.isPending) return;
    try {
      await requestCode.mutateAsync({ email: email.trim() });
      setCode("");
      setStep("code");
      setTimeout(() => codeInputRef.current?.focus(), 50);
    } catch {
      /* surfaced via requestCode.error */
    }
  }

  // ── Step 2: verify the code ─────────────────────────────────────────────────
  async function onVerifyCode(e: FormEvent) {
    e.preventDefault();
    if (code.length !== 6 || verifyCode.isPending) return;
    try {
      const res = await verifyCode.mutateAsync({ email: email.trim(), code });
      if ("signupToken" in res) {
        // New account — go set a password.
        setSignupToken(res.signupToken);
        setStep("password");
      } else {
        // Existing user — logged in.
        goTo(next ?? defaultTarget(res.role), res.mustChangePassword);
      }
    } catch {
      /* surfaced via verifyCode.error */
    }
  }

  // ── Step 3: set the password (new accounts only) ────────────────────────────
  async function onCompleteSignup(e: FormEvent) {
    e.preventDefault();
    if (password.length < 8 || completeSignup.isPending) return;
    try {
      const res = await completeSignup.mutateAsync({
        signupToken,
        password,
        name: name.trim() || undefined,
      });
      goTo(next ?? defaultTarget(res.role), res.mustChangePassword);
    } catch {
      /* surfaced via completeSignup.error */
    }
  }

  // ── Google ──────────────────────────────────────────────────────────────────
  async function onGoogleCredential(credential: string) {
    setGoogleError(null);
    try {
      const res = await googleAuth.mutateAsync({ credential });
      goTo(next ?? defaultTarget(res.role), res.mustChangePassword);
    } catch (err) {
      setGoogleError(authErrorMessage(err) ?? "Google sign-in failed.");
    }
  }

  const busy =
    requestCode.isPending ||
    verifyCode.isPending ||
    completeSignup.isPending ||
    googleAuth.isPending;

  return (
    <AuthShell footer={<AuthLegalLinks />}>
      <div className="animate-fade-in-up rounded-2xl border border-border bg-card/70 p-6 shadow-card backdrop-blur md:p-8">
        {/* ── EMAIL STEP ── */}
        {step === "email" && (
          <>
            <h1 className="font-display text-3xl font-black tracking-tight">
              Create your workspace
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Enter your email — we'll send a 6-digit code to get started.
            </p>

            <form onSubmit={onRequestCode} className="mt-7 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                />
              </div>

              {requestCode.error && (
                <div
                  role="alert"
                  className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {authErrorMessage(requestCode.error)}
                </div>
              )}

              <Button type="submit" size="lg" className="group w-full" disabled={!email || busy}>
                {requestCode.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending code…
                  </>
                ) : (
                  <>
                    Continue with email
                    <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>
            </form>

            <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              or
              <span className="h-px flex-1 bg-border" />
            </div>

            <div className="flex justify-center">
              <GoogleSignInButton onCredential={onGoogleCredential} disabled={busy} />
            </div>
            {(googleError || googleAuth.isPending) && (
              <div className="mt-3 text-center text-sm text-muted-foreground">
                {googleAuth.isPending ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Signing in…
                  </span>
                ) : (
                  <span className="text-destructive">{googleError}</span>
                )}
              </div>
            )}
          </>
        )}

        {/* ── CODE STEP ── */}
        {step === "code" && (
          <>
            <MailCheck className="h-9 w-9 text-brand" strokeWidth={1.5} />
            <h1 className="mt-3 font-display text-3xl font-black tracking-tight">
              Check your email
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              We sent a 6-digit code to <span className="text-foreground">{email}</span>.
            </p>

            <form onSubmit={onVerifyCode} className="mt-7 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="code">6-digit code</Label>
                <Input
                  id="code"
                  ref={codeInputRef}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  disabled={verifyCode.isPending}
                  className="text-center text-lg tracking-[0.5em]"
                  placeholder="••••••"
                />
              </div>

              {verifyCode.error && (
                <div
                  role="alert"
                  className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {authErrorMessage(verifyCode.error)}
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                className="group w-full"
                disabled={code.length !== 6 || verifyCode.isPending}
              >
                {verifyCode.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying…
                  </>
                ) : (
                  <>
                    Verify
                    <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-5 flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => {
                  setStep("email");
                  verifyCode.reset();
                }}
                className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Change email
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!requestCode.isPending) requestCode.mutate({ email: email.trim() });
                }}
                disabled={requestCode.isPending}
                className="text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
              >
                {requestCode.isPending ? "Resending…" : "Resend code"}
              </button>
            </div>
          </>
        )}

        {/* ── PASSWORD STEP ── */}
        {step === "password" && (
          <>
            <h1 className="font-display text-3xl font-black tracking-tight">
              Set a password
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              You'll use this to sign in next time. At least 8 characters.
            </p>

            <form onSubmit={onCompleteSignup} className="mt-7 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">
                  Your name{" "}
                  <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={completeSignup.isPending}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="new-password">Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    autoFocus
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={completeSignup.isPending}
                    className="pr-9"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 grid w-9 place-items-center text-muted-foreground transition-colors hover:text-foreground"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {password.length > 0 && password.length < 8 && (
                  <p className="text-xs text-muted-foreground">
                    {8 - password.length} more character{8 - password.length === 1 ? "" : "s"}.
                  </p>
                )}
              </div>

              {completeSignup.error && (
                <div
                  role="alert"
                  className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {authErrorMessage(completeSignup.error)}
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                className="group w-full"
                disabled={password.length < 8 || completeSignup.isPending}
              >
                {completeSignup.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating workspace…
                  </>
                ) : (
                  <>
                    Create workspace
                    <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>
            </form>
          </>
        )}
      </div>

      <div className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account?{" "}
        <Link
          to="/login"
          className="font-medium text-foreground transition-colors hover:text-brand"
        >
          Sign in
        </Link>
      </div>
    </AuthShell>
  );
}
